import React from 'react';
import {
  Component,
  PuzzleCheckbox,
  PuzzleInput,
  PuzzleTooltip as TpTooltip,
  WithTooltip
} from "puzzle"
import { GridCellProps, GridColumn as Column } from "@progress/kendo-react-grid";
import { DATA_TYPE, REQUEST_OBJECT, RESPONSE, SELECTABLE_DATA, SOURCE_FILES_STATE } from './Types'
import virtualScroll from "../../../VirtualScroll";
import { getTableTdAccessibilityProps } from "../../../util/Util";

import { Keys } from "@progress/kendo-react-common";
import KendoGridAbstract from "../../../util/KendoGridAbstract/KendoGridAbstract";

class SourceFilesDialog extends Component<{}, SOURCE_FILES_STATE> {
  gridRef: any;
  searchDelayTimer: any;
  tooltip: any;

  static defaultProps = {
    pageSize: 20,
    requestActionName: 'getNextPageData',
    rowHeight: 40,
  };

  public state: SOURCE_FILES_STATE = {
    data: [],
    pageDataPopulated: [],
    pageNumber: 0,
    requestInProgress: false,
    skip: 0,
    total: 0,
    documentIds: [],
    filterText: ''
  };

  /**
   * Sending params for Download selected button
   */
  getParamsDownloadSelectedFiles = () => {
    const { documentIds } = this.state;
    return documentIds.length > 0 && {
      sourceFiles: true,
      sourceDocumentIds: documentIds
    }
  }

  /**
   *Sending params for Download All button
   */
  getParamsDownloadAll = () => ({ sourceFiles: true });

  /**
   * Method executes after setting filter state
   */
  filterChangeCallback = (): void => {
    clearTimeout(this.searchDelayTimer);
    (this.searchDelayTimer as any) = setTimeout(this.filterData, 300);
  };

  /**
   * Dispatches ajax request for new data with current filter
   */
  filterData = (): void => {
    const requestParams = this.prepareRequestParams();
    this.dispatch('getSourceFilesWithFilter', '', requestParams);
  };

  /**
   * Files Name cell renderer
   */
  filesNameRender = (cellProps: GridCellProps) => {
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      <td
        title={cellProps.dataItem.name}
        className="tp-100"
        {...tdProps}
      >
        <WithTooltip
          className={'tp-file-name-cell tp-100'}
          context={this}
          title={cellProps.dataItem.name}
          htmlTag={'span'}
        >
          <span title={cellProps.dataItem.name}>
            {cellProps.dataItem.name}
          </span>
        </WithTooltip>
      </td>
    )
  };

  /**
   * Page size getter
   */
  getPageSize = (): string => this.props.pageSize;
  
  /**
   * Getter for selected record
   */
  getSelectedData = () => {
    const { data } = this.state;
    const selectedData = data.filter((item) => item.selected);
    return selectedData.length;
  };

  /**
   * Handles input field change
   * @param event
   */
  handleChange = (event: any): void => {
    this.setState({
      filterText: event.value
    }, this.filterChangeCallback);
  };

  /**
   * Scrolls grid to the top on input focus to ensure next tab will focus first record in the grid
   *
   */
  inputSearchOnFocus = (): void => {
    const elementsByClassName = this.gridRef.element?.getElementsByClassName('k-grid-content');
    if (elementsByClassName.length) {
      elementsByClassName[0].scrollTop = 0;
    }
  };

  /**
   * Makes request params(method is required by virtualScroll hoc)
   * @param pageNumber
   */

  prepareRequestParams = (pageNumber?: number): REQUEST_OBJECT => {
    const { pageSize } = this.props;
    const { filterText } = this.state;
    let requestParams: REQUEST_OBJECT = {
      params: {
        pageSize: pageSize,
        orderBy: 'name'
      }
    };

    Object.assign(requestParams.params,
      filterText ? { documentName: filterText } : null,
      pageNumber ? { pageNumber } : null
    );

    return requestParams;
  };
  /**
   * Grid Selection Change handler
   * @param dataItem
   */
  selectionChange = (dataItem: SELECTABLE_DATA): void => {
    const { data } = this.state;
    let arrayIndex = data.findIndex((currentValue) => currentValue === dataItem);
    let item = data[arrayIndex];
    item.selected = !dataItem.selected;
    let documentIds: string[] = [...this.state.documentIds];
    let id = documentIds.findIndex((currentValue) => currentValue === dataItem.documentId);
    id >= 0 ? documentIds.splice(id, 1) : documentIds.push(dataItem.documentId);
    this.setState({
      data,
      documentIds,
    }, () => {
      this.dispatch('updateDownloadSourceFilesButtons', '', { total: data.length, selectedFiles: documentIds.length })
    })
  };

  /**
   * Sets Source files grid data.
   * @param data
   */
  setGridData = (data: RESPONSE): void => {
    const { documentIds } = this.state;
    data.items = data.items.map((item: DATA_TYPE): SELECTABLE_DATA => {
      return {
        ...item,
        selected: documentIds.includes(item.documentId)
      }
    });
    this.props.virtualScroll.setGridData(data);
  };

  /**
   * Reset's SourceFilesDialog state params
   */
  resetGridData = (): void => {
    this.setState({
      data: [],
      pageNumber: 0,
      skip: 0,
      pageDataPopulated: []
    });
  };

  /**
   * Selection cell render
   * @param cellProps
   */
  selectCellRenderer = (cellProps: GridCellProps): JSX.Element => {
    const { dataItem } = cellProps;
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      <td
        className={'tp-select-file'}
        {...tdProps}
        onKeyDown={this.selectionColumnOnKeyDown}
      >
        <PuzzleCheckbox
          className='k-d-flex k-align-items-center k-justify-content-center'
          checked={dataItem['selected']}
          onChange={this.selectionChange.bind(this, dataItem)}
          label={this.translate('SRL-checkboxFileName', { fileName: dataItem.name })}
        />
      </td>
    )
  };

  /**
   * Supports shift+tab to navigate in back order
   *
   * @param e
   */
  selectionColumnOnKeyDown = (e: any): void => {
    if (Keys.tab === e.keyCode && e.shiftKey) {
      e.target.scrollIntoView({
        behavior: "auto",
        block: "center",
        inline: "nearest"
      });
    }
  };

  /**
   * Renders placeholder in header for first (select) column
   */
  selectHeaderCellRender = () => {
    return <div className='pd-indetermintate-disabled-checkbox tp-icon tp-remove' tabIndex={0}>
      <span className={'sr-only'}>{this.translate('SRL-fileSelection')}</span>
    </div>
  }

  renderBody = () => {
    let { data, filterText, total, skip } = this.state;
    let { pageSize, rowHeight } = this.props;
    return (
      <>
        <PuzzleInput
          searchField
          aria-label={this.translate('search')}
          className={'tp-source-files-input'}
          id={"searchFiles"}
          name="searchFiles"
          onFocus={this.inputSearchOnFocus}
          onChange={this.handleChange}
          placeholder={this.translate('search')}
          value={filterText}
        />
        <KendoGridAbstract
          captionText={this.translate('SRL-downloadSourceFilesTableCaption')}
          ref={(el) => this.gridRef = el?.gridRef}
          className={'tp-source-files-grid'}
          data={data.slice(skip, skip + pageSize)}
          onPageChange={this.props.virtualScroll.pageChange}
          pageSize={pageSize}
          resizable={false}
          rowHeight={rowHeight}
          scrollable={'virtual'}
          selectedField="selected"
          skip={skip}
          total={total}
        >
          <Column
            filterable={false}
            cell={this.selectCellRenderer}
            headerCell={this.selectHeaderCellRender}
            width="50px"
          />
          <Column
            field="name"
            filterable={false}
            cell={this.filesNameRender}
            title={this.translate('columnHeader.fileName')}
          />
        </KendoGridAbstract>
        <TpTooltip
          anchorElement={'target'}
          ref={(el: any) => this.tooltip = el}
          openDelay={1000}
          position="top"
        />
      </>
    );
  }
}

export default virtualScroll(SourceFilesDialog);
